'use client';

import { bunja } from 'bunja';
import { createScopeFromContext, useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { PropsWithChildren, createContext } from 'react';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { CookieKeys, cookieStore } from '@flipster/utils/cookie';

import { getAppInfo } from './app-info';

export { getAppInfo };

/**
 * 웹뷰 여부를 반환합니다.
 */
export function useIsWebview(): boolean {
  const { webviewAtom } = useBunja(webviewInfoBunja);
  return useAtomValue(webviewAtom);
}

/**
 * 웹뷰 여부와 app version을 반환합니다.
 */
export function useWebviewInfo(): WebviewInfo {
  const { webviewInfoAtom } = useBunja(webviewInfoBunja);
  return useAtomValue(webviewInfoAtom);
}
export interface WebviewInfo {
  isReady: boolean;
  webview: boolean;
  appVersion: string;
  osType: 'Android' | 'iOS' | 'unknown';
  osVersion: number;
}

/**
 * 다른 값(search params, session storage)들을 확인하지 않아도
 * 웹뷰임을 명확히 알 수 있는 경우에 그 정보를 주입하기 위해 사용합니다.
 */
export const IsWebviewContext = createContext<boolean>(false);
export const IsWebviewScope = createScopeFromContext(IsWebviewContext);
export const IsWebviewProvider = ({ value = true, children }: PropsWithChildren<{ value?: boolean }>) => {
  return <IsWebviewContext.Provider value={value}>{children}</IsWebviewContext.Provider>;
};

export const webviewInfoBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const isWebview = bunja.use(IsWebviewScope);
  const isReadyAtom = atom(false);
  const webviewAtom = atom(isWebview);
  const appVersionAtom = atom('2');
  const osTypeAtom = atom<'Android' | 'iOS' | 'unknown'>('unknown');
  const osVersionAtom = atom(0);

  const webviewInfoAtom = atom<WebviewInfo>((get) => {
    const isReady = get(isReadyAtom);
    const webview = get(webviewAtom);
    const appVersion = get(appVersionAtom);
    const osType = get(osTypeAtom);
    const osVersion = get(osVersionAtom);
    return { isReady, webview, appVersion, osType, osVersion };
  });

  bunja.effect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const webview = searchParams.get(keys.webview.searchParamsKey);
    const appVersion = searchParams.get(keys.appVersion.searchParamsKey);
    const osType = searchParams.get(keys.osType.searchParamsKey);
    const osVersion = searchParams.get(keys.osVersion.searchParamsKey);
    if (webview) sessionStorage.setItem(keys.webview.storageKey, webview);
    if (appVersion) sessionStorage.setItem(keys.appVersion.storageKey, appVersion);
    if (osType) sessionStorage.setItem(keys.osType.storageKey, osType);
    if (osVersion) sessionStorage.setItem(keys.osVersion.storageKey, osVersion);
    store.set(webviewAtom, isWebview || getField('webview'));
    store.set(appVersionAtom, getField('appVersion'));
    store.set(osTypeAtom, getField('osType'));
    store.set(osVersionAtom, getField('osVersion'));
    store.set(isReadyAtom, true);
    return () => {};
  });

  return {
    isReadyAtom,
    webviewAtom,
    appVersionAtom,
    webviewInfoAtom,
  };
});
webviewInfoBunja.debugLabel = 'webviewInfoBunja';

/**
 * 웹뷰 정보를 search params로부터 읽어서
 * session storage에 저장해주는 컴포넌트입니다.
 */
export const InitWebviewInfo = () => {
  useBunja(webviewInfoBunja);
  return null;
};

/**
 * 각 필드를 읽을 때 어떤 채널부터 읽을지를 결정합니다.
 */
const readPriorities = ['getAppInfo', 'sessionStorage', 'cookie'] as const;
type Channel = (typeof readPriorities)[number];
type Field = 'webview' | 'appVersion' | 'osType' | 'osVersion';

type CoercedField<T extends Field> = ReturnType<(typeof coerceFns)[T]>;
type CoerceFn<T> = (value: string | null) => T;
const coerceFns = {
  webview: (value: string | null) => value === 'true',
  appVersion: (value: string | null) => value || '2',
  osType: (value: string | null) => (value || 'unknown') as 'Android' | 'iOS' | 'unknown',
  osVersion: (value: string | null) => Number(value || 0),
} as const satisfies { [K in Field]: CoerceFn<unknown> };

const keys = {
  webview: {
    storageKey: 'FLIPSTER_WEBVIEW',
    searchParamsKey: 'webview',
    cookieKey: CookieKeys.Webview,
  },
  appVersion: {
    storageKey: 'FLIPSTER_APP_VERSION',
    searchParamsKey: 'appVersion',
    cookieKey: CookieKeys.AppVersion,
  },
  osType: {
    storageKey: 'FLIPSTER_OS_TYPE',
    searchParamsKey: 'osType',
    cookieKey: CookieKeys.OsType,
  },
  osVersion: {
    storageKey: 'FLIPSTER_OS_VERSION',
    searchParamsKey: 'osVersion',
    cookieKey: CookieKeys.OsVersion,
  },
} as const satisfies {
  [K in Field]: {
    storageKey: string;
    searchParamsKey: string;
    cookieKey: string;
  };
};

function getField<T extends Field>(field: T): CoercedField<T> {
  return coerceFns[field](getRawValue(field)) as CoercedField<T>;
}

function getRawValue(field: Field): string | null {
  for (const channel of readPriorities) {
    const value = getValueFrom(channel, field);
    if (value) return value;
  }
  return null;
}

function getValueFrom(channel: Channel, field: Field): string | null {
  switch (channel) {
    case 'getAppInfo': {
      const appInfo = getAppInfo();
      if (!appInfo) return null;
      if (field === 'webview') return 'true'; // `getAppInfo`의 반환값이 존재하면 항상 웹뷰임
      if (field === 'appVersion') return appInfo.appVersion;
      if (field === 'osType') return appInfo.device;
      return null;
    }
    case 'sessionStorage':
      if (typeof sessionStorage === 'undefined') return null;
      return sessionStorage.getItem(keys[field].storageKey) || null;
    case 'cookie':
      return cookieStore.get(keys[field].cookieKey) || null;
  }
}
